import * as apiService from "./v2/apiService";
import { baseURL } from "@/constants/v2/endPoints";

const MANAGE_URL = `${baseURL}api/v2/brokerage/manage`;

export const LOGO_TYPES = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
};

export const LOGO_MAX_BYTES = 2 * 1024 * 1024;
export const COVER_MAX_BYTES = 5 * 1024 * 1024;

// apiCall is a plain axios call, so the brokerage API's 400/403/404 replies reject.
// Hand back the API body ({ errorCode, message, data }) so callers can show its
// message and field errors; anything without one (network, gateway) still throws.
const call = async (...args) => {
  try {
    return await apiService.apiCall(...args);
  } catch (error) {
    const body = error?.response?.data;
    if (body && typeof body === "object" && body.errorCode) return body;
    throw error;
  }
};

export async function getMyBrokerage() {
  return await apiService.apiCall(`${baseURL}api/v2/brokerage/mine`, "GET", {});
}

export async function getManagedBrokerages() {
  return await call(MANAGE_URL, "GET", {});
}

export async function getManagedBrokerage(id) {
  return await call(`${MANAGE_URL}/${id}`, "GET", {});
}

export async function saveBrokerageBranding(id, branding) {
  return await call(`${MANAGE_URL}/${id}`, "PUT", branding);
}

export async function saveAgentOrder(id, order) {
  return await call(`${MANAGE_URL}/${id}/agent-order`, "PUT", order);
}

export async function saveHiddenAgents(id, userIds) {
  return await apiService.apiCall(`${MANAGE_URL}/${id}/hidden`, "PUT", { userIds });
}

export async function saveBrokerageTeams(id, teams) {
  return await apiService.apiCall(`${MANAGE_URL}/${id}/teams`, "PUT", { teams });
}

export async function addBrokerageManager(id, userId) {
  return await call(`${MANAGE_URL}/${id}/admins`, "POST", { userId });
}

export async function removeBrokerageManager(id, userId) {
  return await call(`${MANAGE_URL}/${id}/admins/${userId}`, "DELETE", {});
}

export async function startBrokerageLogoUpload(id, contentType, size, kind = "logo") {
  return await call(`${MANAGE_URL}/${id}/logo-upload`, "POST", { contentType, size, kind });
}

const UPLOAD_LIMITS = {
  logo: { maxBytes: LOGO_MAX_BYTES, typeError: "Use a PNG, JPG or WebP logo.", sizeError: "Use a logo under 2 MB." },
  cover: { maxBytes: COVER_MAX_BYTES, typeError: "Use a JPG, PNG or WebP cover image.", sizeError: "Use a cover image under 5 MB." },
};

export async function uploadBrokerageLogo(brokerageId, file, kind = "logo") {
  const limits = UPLOAD_LIMITS[kind] || UPLOAD_LIMITS.logo;
  const ext = LOGO_TYPES[file?.type];
  if (!ext) throw new Error(limits.typeError);
  if (file.size > limits.maxBytes) throw new Error(limits.sizeError);
  const response = await startBrokerageLogoUpload(brokerageId, file.type, file.size, kind);
  const ticket = response?.errorCode === 0 ? response.result : null;
  if (!ticket?.uploadUrl || !ticket?.url) throw new Error(response?.message || "Could not start the upload.");
  const upload = await fetch(ticket.uploadUrl, {
    method: "PUT",
    headers: new Headers({ "Content-Type": file.type }),
    body: file,
  });
  if (!upload.ok) throw new Error(kind === "cover" ? "The cover image did not upload. Please try again." : "The logo did not upload. Please try again.");
  return ticket.url;
}

export async function getOfficeResults(id, days) {
  return await call(`${MANAGE_URL}/${id}/results?days=${Number(days) || 30}`, "GET", {});
}

export async function getLeadRouting(id) {
  return await call(`${MANAGE_URL}/${id}/lead-routing`, "GET", {});
}

export async function saveLeadRouting(id, routing) {
  return await call(`${MANAGE_URL}/${id}/lead-routing`, "PUT", routing);
}

export async function startBrokeragePlanCheckout(id, { period = "month", seatUserIds = [] } = {}) {
  return await call(`${MANAGE_URL}/${id}/plan/checkout`, "POST", { period, seatUserIds });
}

export async function confirmBrokeragePlanCheckout(id, sessionId) {
  return await call(`${MANAGE_URL}/${id}/plan/checkout/confirm`, "POST", { sessionId });
}

export async function openBrokeragePlanPortal(id) {
  return await call(`${MANAGE_URL}/${id}/plan/portal`, "POST", {});
}

export async function saveBrokerageSeats(id, seatUserIds) {
  return await call(`${MANAGE_URL}/${id}/plan/seats`, "PUT", { seatUserIds });
}

export async function getProRequests(id) {
  return await call(`${MANAGE_URL}/${id}/pro-requests`, "GET", {});
}

export async function declineProRequest(id, requestId) {
  return await call(`${MANAGE_URL}/${id}/pro-requests/${requestId}/decline`, "POST", {});
}
